/**
 * RegisterCompleteScreen — iter1097
 *
 * Финална „Готов си!" celebration screen. Показва се 2 секунди след като
 * user-a завърши Health Assessment, преди редирект към Dashboard.
 *
 * Създава explicit „you're done" момент — според SaaS PM best-practice
 * това увеличава retention D1 защото user-a има ясно „arrival" усещане.
 *
 * Използва RegisterProgressStepper с currentStep=4 (all done).
 */
import React, { useEffect } from "react";
import confetti from "canvas-confetti";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import RegisterProgressStepper from "@/components/RegisterProgressStepper";
import { Check, Sparkles } from "lucide-react";

/**
 * @param {() => void} onDone — извиква се след `delayMs` (default 2000ms).
 * @param {boolean} [skipCycle] — форуардва към stepper-a за да съвпада с
 *   предишните му state-ове (мъже виждат 3-step version).
 * @param {number} [delayMs] — default 2000.
 */
export default function RegisterCompleteScreen({ onDone, skipCycle = false, delayMs = 2000 }) {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";

  useEffect(() => {
    // iter1098 — Confetti burst: 3 залпа (център + 2 странични) за „arrival"
    // feeling. Emerald/fuchsia палитра за brand consistency.
    const colors = ["#10b981", "#14b8a6", "#a855f7", "#ec4899", "#f59e0b"];
    const timers = [];
    try {
      confetti({
        particleCount: 90,
        spread: 70,
        origin: { y: 0.7, x: 0.5 },
        colors,
        zIndex: 9999,
        scalar: 0.9,
        ticks: 200,
      });
      timers.push(setTimeout(() => {
        try {
          confetti({
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.75 },
            colors,
            zIndex: 9999,
            scalar: 0.8,
            ticks: 180,
          });
        } catch { /* noop */ }
      }, 200));
      timers.push(setTimeout(() => {
        try {
          confetti({
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.75 },
            colors,
            zIndex: 9999,
            scalar: 0.8,
            ticks: 180,
          });
        } catch { /* noop */ }
      }, 400));
    } catch { /* confetti е cosmetic — не блокира flow-a */ }

    const t = setTimeout(() => {
      if (onDone) onDone();
    }, delayMs);
    return () => {
      clearTimeout(t);
      timers.forEach((x) => clearTimeout(x));
    };
  }, [onDone, delayMs]);

  return (
    <div
      data-testid="register-complete-screen"
      className={`min-h-screen flex items-center justify-center p-6 ${
        isDark
          ? "bg-gradient-to-br from-[hsl(222,47%,8%)] via-[hsl(240,30%,12%)] to-[hsl(222,47%,10%)]"
          : "bg-gradient-to-br from-emerald-50 via-white to-teal-50"
      }`}
    >
      <div className="w-full max-w-md text-center">
        {/* Stepper — step 4 (Готов) е ACTIVE, всички преди са done */}
        <div className="mb-8">
          <RegisterProgressStepper currentStep={4} skipCycle={skipCycle} />
        </div>

        {/* Icon */}
        <div className="relative inline-flex items-center justify-center mb-6">
          <div
            className={`absolute inset-0 rounded-full blur-2xl opacity-70 ${
              isDark ? "bg-emerald-400/40" : "bg-emerald-300"
            } animate-pulse`}
          />
          <div
            data-testid="register-complete-check"
            className={`relative w-24 h-24 rounded-full flex items-center justify-center ${
              isDark
                ? "bg-gradient-to-br from-emerald-500 to-teal-500"
                : "bg-gradient-to-br from-emerald-500 to-teal-500"
            } shadow-2xl shadow-emerald-500/40`}
          >
            <Check className="w-14 h-14 text-white" strokeWidth={3} />
          </div>
        </div>

        {/* Copy */}
        <h1
          className={`text-3xl sm:text-4xl font-black mb-3 ${
            isDark ? "text-white" : "text-slate-900"
          }`}
          data-testid="register-complete-title"
        >
          {bg ? "Готов си! 🎉" : "You're ready! 🎉"}
        </h1>
        <p
          className={`text-sm sm:text-base leading-relaxed mb-6 ${
            isDark ? "text-slate-300" : "text-slate-600"
          }`}
          data-testid="register-complete-subtitle"
        >
          {bg
            ? "Твоят Nutramilo twin е настроен. Отвеждаме те към Dashboard-a..."
            : "Your Nutramilo twin is set up. Taking you to your Dashboard..."}
        </p>

        {/* Loader */}
        <div className="inline-flex items-center gap-2 text-xs">
          <Sparkles
            className={`w-4 h-4 animate-pulse ${
              isDark ? "text-fuchsia-300" : "text-fuchsia-600"
            }`}
          />
          <span className={isDark ? "text-slate-400" : "text-slate-500"}>
            {bg ? "Зарежда се твоят wellness план" : "Loading your wellness plan"}
          </span>
        </div>
      </div>
    </div>
  );
}
